import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'bhs_cookie_consent';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (type, prefs = null) => {
    const consentData = {
      type,
      preferences: prefs || preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true };
    setPreferences(allAccepted);
    saveConsent('accept_all', allAccepted);
  };

  const handleReject = () => {
    const onlyNecessary = { necessary: true, analytics: false, marketing: false };
    setPreferences(onlyNecessary);
    saveConsent('reject', onlyNecessary);
  };

  const handleSavePreferences = () => {
    saveConsent('custom', preferences);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Necessary cookies cannot be disabled
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
      >
        <div className="max-w-4xl mx-auto">
          <div 
            className="bg-[#FAFAF8] border border-stone-200 rounded-2xl shadow-xl overflow-hidden"
            style={{ boxShadow: '0 -4px 30px rgba(15, 35, 54, 0.12)' }}
          >
            {/* Main Banner */}
            {!showPreferences ? (
              <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  {/* Text Content */}
                  <div className="flex-1">
                    <h3 className="font-heading text-lg text-navy mb-2">
                      Cookie Preferences
                    </h3>
                    <p className="text-stone-600 text-sm leading-relaxed">
                      We use cookies to improve your browsing experience and analyze website traffic. 
                      You can accept all cookies or manage your preferences.
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                    <button
                      onClick={() => setShowPreferences(true)}
                      className="px-5 py-2.5 text-xs font-medium uppercase tracking-wider text-navy border border-navy/30 rounded-lg hover:bg-navy/5 transition-colors"
                    >
                      Manage Preferences
                    </button>
                    <button
                      onClick={handleReject}
                      className="px-5 py-2.5 text-xs font-medium uppercase tracking-wider text-stone-600 border border-stone-300 rounded-lg hover:bg-stone-100 transition-colors"
                    >
                      Reject
                    </button>
                    <button
                      onClick={handleAcceptAll}
                      className="px-6 py-2.5 text-xs font-medium uppercase tracking-wider text-white bg-navy rounded-lg hover:bg-navy/90 transition-colors"
                    >
                      Accept All
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Preferences Panel */
              <div className="p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading text-lg text-navy">
                    Manage Cookie Preferences
                  </h3>
                  <button 
                    onClick={() => setShowPreferences(false)}
                    className="p-1 text-stone-400 hover:text-navy transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Necessary Cookies */}
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-stone-100">
                    <div className="flex-1 pr-4">
                      <h4 className="text-sm font-medium text-navy mb-1">Necessary Cookies</h4>
                      <p className="text-xs text-stone-500">Required for the website to function properly. Cannot be disabled.</p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-12 h-6 bg-navy rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-stone-100">
                    <div className="flex-1 pr-4">
                      <h4 className="text-sm font-medium text-navy mb-1">Analytics Cookies</h4>
                      <p className="text-xs text-stone-500">Help us understand how visitors interact with the website.</p>
                    </div>
                    <button 
                      onClick={() => togglePreference('analytics')}
                      className="flex-shrink-0"
                    >
                      <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${preferences.analytics ? 'bg-navy justify-end' : 'bg-stone-300 justify-start'}`}>
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm"></div>
                      </div>
                    </button>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-stone-100">
                    <div className="flex-1 pr-4">
                      <h4 className="text-sm font-medium text-navy mb-1">Marketing Cookies</h4>
                      <p className="text-xs text-stone-500">Used to deliver personalized content and advertisements.</p>
                    </div>
                    <button 
                      onClick={() => togglePreference('marketing')}
                      className="flex-shrink-0"
                    >
                      <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${preferences.marketing ? 'bg-navy justify-end' : 'bg-stone-300 justify-start'}`}>
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm"></div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Save Preferences Button */}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="px-5 py-2.5 text-xs font-medium uppercase tracking-wider text-stone-600 border border-stone-300 rounded-lg hover:bg-stone-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="px-6 py-2.5 text-xs font-medium uppercase tracking-wider text-white bg-navy rounded-lg hover:bg-navy/90 transition-colors"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
